import { MenuItem, Button, ButtonGroup, Grow, Paper, MenuList, Popper, Stack, CircularProgress } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { SupportedLanguageCodes } from "@skibro/types";
import React, { useState } from "react";
import { useContent } from "../Context/ContentContext";
import { useTranslation } from "react-i18next";
import { UseMutationResult, useMutation } from "react-query";
import api from "../lib/api";
import { Translate } from "@mui/icons-material";

export const TranslateControl: React.FC<{
  sourceText: { [x: string]: string };
  to: SupportedLanguageCodes;
  onTranslate: (translation, language) => void;
}> = ({ sourceText, to, onTranslate }) => {
  const { languages } = useContent();
  const { t } = useTranslation();
  const [sourceLanguage, setSourceLanguage] = useState<SupportedLanguageCodes>();
  const translateMutation: UseMutationResult<
    any,
    unknown,
    {
      text: string;
      from: SupportedLanguageCodes;
      to: SupportedLanguageCodes;
    },
    void
  > = useMutation(
    (body) => {
      return api.translate(body);
    },
    {
      onSuccess: async (result) => {
        onTranslate(result, to);
      },
    }
  );
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handleClick = (event) => {
    translateMutation.mutate({ from: sourceLanguage, to, text: sourceText[sourceLanguage] });
  };

  const handleMenuItemClick = (language) => {
    setSourceLanguage(language);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const dropDownDisabled = !Object.keys(sourceText).filter((l) => !!sourceText[l] && l !== to).length;

  return (
    <Stack direction={"row"} justifyContent={"right"}>
      <Paper
        variant="outlined"
        sx={{
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          p: 1,
          borderTop: 0,
        }}
      >
        <ButtonGroup size="small" variant="outlined" ref={anchorRef} aria-label="translate button">
          <Button
            color="secondary"
            startIcon={translateMutation.isLoading ? <CircularProgress size={18} /> : <Translate />}
            size="small"
            onClick={handleClick}
            disabled={!sourceLanguage || sourceLanguage === to || !sourceText[sourceLanguage]}
          >
            {sourceLanguage && sourceText[sourceLanguage] && sourceLanguage !== to
              ? t("{{from}} to {{to}}", {
                  to: languages?.find((l) => l.code === to)?.name,
                  from: languages?.find((l) => l.code === sourceLanguage)?.name,
                })
              : t("Translate")}
          </Button>
          <Button
            disabled={dropDownDisabled}
            size="small"
            color="secondary"
            aria-controls={open ? "language-translate-menu" : undefined}
            aria-expanded={open ? "true" : undefined}
            aria-label="Choose language"
            aria-haspopup="menu"
            onClick={handleToggle}
          >
            <ArrowDropDownIcon color="inherit" />
          </Button>
        </ButtonGroup>
        <Popper
          sx={{
            zIndex: 1,
          }}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === "bottom" ? "center top" : "center bottom",
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="language-translate-menu" autoFocusItem>
                    {Object.keys(sourceText).map(
                      (language) =>
                        language !== to &&
                        sourceText[language] && (
                          <MenuItem
                            key={language}
                            value={language}
                            selected={language === sourceLanguage}
                            onClick={(event) => handleMenuItemClick(language)}
                          >
                            Translate From {languages?.find((l) => l.code === language)?.name || language}
                          </MenuItem>
                        )
                    )}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Paper>
    </Stack>
  );
};
